import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useLogin from "../../Autenticacion/Hooks/useLogin";
import { useToast } from "../../../Hooks/useToast";
import { ParametroObtenerPorIdService } from "../../../Services/ParametroObtenerPorIdService";
import { EnumPathRutas } from "../../../Utils/Enumeradores/ComunEnum";
import { useBackupBaseDato } from "../Hooks/useBackupBaseDato";
import ModalDescargaBaseDatos from "../Components/ModalDescargaBaseDatos";
import { GenerarUrlDescargaUnicaBackupService } from "../Services/GenerarUrlDescargaUnicaBackupService";
import { ObtenerUrlDescargaBackupService } from "../Services/ObtenerUrlDescargaBackupService";
import "./BackupBaseDatoPage.css";

const TRIMESTRES_DEFAULT = [
    { trimestreId: 1, trimestre: "Q1", disponible: false, estado: "No disponible", archivos: [] },
    { trimestreId: 2, trimestre: "Q2", disponible: false, estado: "No disponible", archivos: [] },
    { trimestreId: 3, trimestre: "Q3", disponible: false, estado: "No disponible", archivos: [] },
    { trimestreId: 4, trimestre: "Q4", disponible: false, estado: "No disponible", archivos: [] },
];

const PARAMETRO_DESCARGA_BACKUP_SEGURO = "DescargaBackupSeguro";

const parseBooleanParameter = (value) => {
    if (typeof value === "boolean") {
        return value;
    }

    if (typeof value === "string") {
        const normalizedValue = value.trim().toLowerCase();
        return normalizedValue === "true";
    }

    return false;
};

const BackupBaseDatoPage = () => {
    const addToast = useToast();
    const { usuario } = useLogin();
    const { listadoRespaldo, anioRespaldo, getRespaldoTrimestre } = useBackupBaseDato();
    const [descargaSegura, setDescargaSegura] = useState(true);
    const [modalDescarga, setModalDescarga] = useState({
        show: false,
        trimestre: "",
        fila: null,
    });

    useEffect(() => {
        getRespaldoTrimestre();
    }, [getRespaldoTrimestre]);

    useEffect(() => {
        let mounted = true;

        const fetchDescargaSegura = async () => {
            try {
                const response = await ParametroObtenerPorIdService(PARAMETRO_DESCARGA_BACKUP_SEGURO);

                if (!mounted || !response || response.status !== 200) {
                    return;
                }

                setDescargaSegura(parseBooleanParameter(response.data));
            } catch (error) {
                setDescargaSegura(true);
            }
        };

        fetchDescargaSegura();

        return () => {
            mounted = false;
        };
    }, []);

    const filas = listadoRespaldo.length > 0 ? listadoRespaldo : TRIMESTRES_DEFAULT;

    const handleDescargar = (fila) => {
        setModalDescarga({
            show: true,
            trimestre: fila?.trimestre || "",
            fila,
        });
    };

    const handleCerrarModal = () => {
        setModalDescarga({
            show: false,
            trimestre: "",
            fila: null,
        });
    };

    const submitDownloadUrl = (url) => {
        const link = document.createElement("a");
        link.href = url;
        link.target = "_self";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDescargarBase = async (archivo) => {
        const trimestre = modalDescarga.fila?.trimestre;

        if (!archivo?.nombre || !trimestre || !anioRespaldo || !usuario?.id) {
            addToast({ mostrar: true, mensaje: "No fue posible preparar la descarga solicitada.", titulo: "Error", tipo: "Danger" });
            return;
        }

        try {
            if (descargaSegura) {
                const response = await GenerarUrlDescargaUnicaBackupService({
                    anio: anioRespaldo,
                    trimestre,
                    nombreArchivoDescarga: archivo.nombre,
                });

                if (!response || response.status !== 200 || !response.data?.url) {
                    addToast({ mostrar: true, mensaje: "No fue posible generar la URL única de descarga solicitada.", titulo: "Error", tipo: "Danger" });
                    return;
                }

                submitDownloadUrl(response.data.url);
            } else {
                const response = await ObtenerUrlDescargaBackupService({
                    idUsuario: usuario.id,
                    anio: anioRespaldo,
                    trimestre,
                    nombreArchivoDescarga: archivo.nombre,
                });

                if (!response || response.status !== 200 || !response.data?.url) {
                    addToast({ mostrar: true, mensaje: "No fue posible obtener la URL de descarga solicitada.", titulo: "Error", tipo: "Danger" });
                    return;
                }

                submitDownloadUrl(response.data.url);
            }

            if (!descargaSegura) {
                addToast({ mostrar: true, mensaje: `La descarga de ${archivo.nombre} se está iniciando.`, titulo: "Éxito", tipo: "Success" });
                return;
            }

            addToast({ mostrar: true, mensaje: `La descarga de ${archivo.nombre} se está preparando en el navegador.`, titulo: "Éxito", tipo: "Success" });
        } catch (error) {
            addToast({ mostrar: true, mensaje: "No fue posible descargar el respaldo solicitado.", titulo: "Error", tipo: "Danger" });
        }
    };

    return (
        <div className="container-fluid">
            {/* Breadcrumb */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="breadcrumbs">
                        <ul>
                            <li>
                                <Link to={EnumPathRutas.Inicio}>Inicio</Link>
                            </li>
                            <li>
                                <Link to={EnumPathRutas.Configuracion}>Configuración</Link>
                            </li>
                            <li>
                                <a href="#">Descargar Respaldo de Base de Datos</a>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Contenido */}
            <div className="row">
                <div className="col-12">
                    <div className="bg-white rounded p-4">
                        {/* Badge año actual */}
                        <div className="mb-4">
                            <span className="backup-bd-badge-anio">{anioRespaldo}</span>
                        </div>

                        {/* Tabla */}
                        <div className="table-responsive">
                            <table className="backup-bd-table w-100">
                                <thead>
                                    <tr>
                                        <th className="backup-bd-th backup-bd-th--trimestre">Trimestre</th>
                                        <th className="backup-bd-th backup-bd-th--estado">Estado</th>
                                        <th className="backup-bd-th backup-bd-th--descargar">Descargar</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filas.map((fila) => (
                                        <tr key={fila.trimestreId} className="backup-bd-row">
                                            <td className="backup-bd-td backup-bd-td--trimestre">
                                                {fila.trimestre}
                                            </td>
                                            <td className="backup-bd-td backup-bd-td--estado">
                                                <span
                                                    className={
                                                        fila.disponible
                                                            ? "backup-bd-estado backup-bd-estado--disponible"
                                                            : "backup-bd-estado backup-bd-estado--no-disponible"
                                                    }
                                                >
                                                    {fila.estado}
                                                </span>
                                            </td>
                                            <td className="backup-bd-td backup-bd-td--descargar">
                                                <button
                                                    className="backup-bd-btn-descargar"
                                                    onClick={() => handleDescargar(fila)}
                                                    disabled={!fila.disponible}
                                                    aria-label={`Descargar ${fila.trimestre}`}
                                                    title={fila.disponible ? `Descargar ${fila.trimestre}` : "No disponible para descarga"}
                                                >
                                                    <svg
                                                        width="16"
                                                        height="16"
                                                        viewBox="0 0 16 16"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        aria-hidden="true"
                                                    >
                                                        <path
                                                            d="M8 1v9M8 10l-3-3M8 10l3-3M2 12v1a1 1 0 001 1h10a1 1 0 001-1v-1"
                                                            stroke="currentColor"
                                                            strokeWidth="1.5"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <ModalDescargaBaseDatos
                show={modalDescarga.show}
                onHide={handleCerrarModal}
                anio={anioRespaldo}
                trimestre={modalDescarga.trimestre}
                archivos={modalDescarga.fila?.archivos || []}
                onDownload={handleDescargarBase}
            />
        </div>
    );
};

export default BackupBaseDatoPage;
